import { AppBar, Toolbar, Grid, Typography, Box, IconButton, useMediaQuery } from "@mui/material";
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HistoryIcon from '@mui/icons-material/History';
import { RootState } from "../../../redux/store";
import TextLogo from "../../../logo/textLogo.png"
import Logo from "../../../logo/logo.png"
import FormadaLogo from "../../../logo/logo-formada.png"
import { HomeItemsGrid, LogoImg, TextLogoImg } from "../appBarStyles"
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { HomeBarItem } from "./homeBarItem"
import InfoIcon from "../../../logo/info.png"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { appStyleMode, CompanyTitle } from "../../../helpers/clientConfigs";
import { AppStyle } from "../../../helpers/appStyle";
import MenuIcon from '@mui/icons-material/Menu';
import { LanguageMenu } from "./languageMenu";
import { CustomerInfo } from "orderme-api-integration-client";
import { roundDoubleNumbers } from "../../../helpers/currency";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { CartBarBadgeButton } from "../../shoppingCart/cartBarBadgeButton";
import { closeMenu, openMenu } from "../../../redux/reducers/menuReducer";
import { useTranslation } from "react-i18next";
import { HomeBarLink } from "./homeBarLink";
import { useRouteNavigator } from "../../../routes/useRouteNavigator";
import { AboutUsRoute, LoginRoute, NewsRoute, SalesPointsRoute } from "../../../routes/allRoutes";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.appBar.background,
  color: theme.palette.appBar.color,
  width: "auto",
  minWidth: "300px",
  paddingRight: "48px",
  '@media (max-width: 600px)': {
    paddingRight: "24px"
  },
}));

export function HomeBar() {
  const routeNavigator = useRouteNavigator();
  const dispatch = useAppDispatch();
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const isPasswordRestored: boolean = useAppSelector((state: RootState) => state.authState.isPasswordRestored);
  const isMenuOpened: boolean = useAppSelector((state: RootState) => state.menuState.isMenuOpened);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const isSmallSize = useMediaQuery('(max-width:700px)');
  const isLargeSize = useMediaQuery('(min-width:1400px)');
  const { t } = useTranslation();

  const handleMenuClick = () => {
    if (isMenuOpened)
      dispatch(closeMenu());
    else
      dispatch(openMenu());
  };

  const mobileToolbar = () => {
    return (
      <AppBar sx={{ backgroundColor: "appBar.background", color: "appBar.color", minHeight: "60px" }} position="static">
        <Toolbar>
          <Grid sx={{ pt: "4px" }} container direction="row" justifyContent="space-between" alignItems="center">
            <TextLogoImg width={100} src={TextLogo} alt={CompanyTitle} />
            <HomeItemsGrid>
              <HomeBarItem icon={<LocationOnIcon />} open={() => routeNavigator.goToSalesPoints()} />
              <HomeBarItem icon={<img height={25} width={25} src={InfoIcon} alt="Info" />} open={() => routeNavigator.goToInfo()} />
              <HomeBarItem icon={<SearchIcon />} open={() => routeNavigator.goToSearch()} />
              <HomeBarItem icon={<AccountCircleIcon />} open={() => routeNavigator.goToAccount()} />
              <HomeBarItem icon={<HistoryIcon />} open={() => routeNavigator.goToOrderHistory()} isVisible={isUserLoggedIn && !isPasswordRestored} />
            </HomeItemsGrid>
          </Grid>
        </Toolbar>
      </AppBar>)
  }

  const websiteToolbar = () => {
    return (
      <Grid sx={{ width: '100%', zIndex: 1200, maxWidth: "xl" }} position="fixed">
        <StyledAppBar elevation={0}
          sx={{
            height: isLargeSize ? "140px" : "100px",
            py: "10px"
          }}
          position="static">
          {isLargeSize &&
            <LogoImg width={appStyleMode === AppStyle.Formada ? 250 : 200}
              appStyle={appStyleMode}
              sx={{ mx: "auto", mb: "10px" }}
              src={appStyleMode === AppStyle.Formada ? FormadaLogo : Logo}
              alt={CompanyTitle}
              onClick={() => routeNavigator.goHome()} />
          }
          <Grid container direction="row">
            <Grid item xs container>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={handleMenuClick} sx={{ p: "6px", ml: "-6px", height: "auto" }}>
                  <MenuIcon
                    sx={{
                      color: 'primary.contrastText',
                      width: "50px",
                      height: "50px",
                      '@media (max-width: 400px)': {
                        width: "40px",
                        height: "40px"
                      }
                    }}
                  />
                </IconButton>
                <LanguageMenu></LanguageMenu>
              </Box>
              {!isSmallSize && (isUserLoggedIn ? userBarItem() : loginBarItem())}
            </Grid>
            {isLargeSize ?
              <Grid item xs="auto" sx={{ width: "600px", mr: "50px" }}>
                <Grid sx={{ height: "60px" }} container direction="row" alignItems="center">
                  <HomeBarLink link={SalesPointsRoute} text={t('salesPoint:salesPoints')} />
                  <HomeBarLink link={NewsRoute} text={t('news:menuTitle')} />
                  <HomeBarLink link={AboutUsRoute} text={t('info:aboutUs')} />
                </Grid>
              </Grid> :
              <Box>
                <LogoImg
                  width={appStyleMode === AppStyle.Formada ? 250 : 200}
                  appStyle={appStyleMode}
                  sx={{ ml: "0px" }}
                  src={appStyleMode === AppStyle.Formada ? FormadaLogo : Logo}
                  alt={CompanyTitle}
                  onClick={() => routeNavigator.goHome()} />
              </Box>
            }
            <Grid item xs container alignItems="center" justifyContent="flex-end" sx={{ width: "210px", mr: "-8px" }}>
              {!isSmallSize && (<CartBarBadgeButton />)}
              <HomeBarItem icon={<SearchIcon />} open={routeNavigator.goToSearch} />
            </Grid>
          </Grid>
        </StyledAppBar>
        {isSmallSize && userAndCartBadgeBar()}
      </Grid >)
  }

  const userAndCartBadgeBar = () => {
    return (
      <StyledAppBar elevation={0}
        sx={{ height: "50px" }}
        position="static">
        <Grid container direction="row">
          {isUserLoggedIn ? userBarItem() : loginBarItem()}
          <Grid item xs container alignItems="center" justifyContent="flex-end">
            <CartBarBadgeButton />
          </Grid>
        </Grid>
      </StyledAppBar>)
  }

  const userBarItem = () => {
    return (
      <Grid item xs sx={{ pl: "4px" }} container direction="row" alignItems="center">
        <Typography sx={{ mr: "5px" }}>
          {customerInfo.name}
        </Typography>
        <Grid sx={{ width: "auto" }} container direction="row" alignItems="center">
          <Typography>
            {roundDoubleNumbers(customerInfo.credit)} €
          </Typography>
          {!isPasswordRestored &&
            <IconButton onClick={() => routeNavigator.goToCredits()} sx={{ p: "5px", color: 'white' }}>
              <AddCircleOutlineIcon sx={{ height: "35px", width: "35px" }} />
            </IconButton>
          }
        </Grid>
      </Grid>)
  }

  const loginBarItem = () => {
    return (
      <Box display="flex" alignItems="center" sx={{ ml: "10px" }}>
        <HomeBarLink link={LoginRoute} text={t('account:login')} disableMargin={true} />
      </Box>
    )
  }

  return (
    <Grid>
      {appStyleMode === AppStyle.Mobile ? mobileToolbar() : websiteToolbar()}
    </Grid>
  );
}